import { ApiClient, ApiLogEntry, ApiRegisterContact, ApiRegisterExclusion } from '../clients/api';

const USE_API = process.env.REACT_APP_USE_API_WRITES === 'true';
const USE_API_READS = process.env.REACT_APP_USE_API_READS === 'true';

/**
 * Escritas que ja tem endpoint no backend.
 *
 * A flag REACT_APP_USE_API_WRITES decide, por deploy de front, se o caminho
 * vale -- o SDK continua sendo o padrao enquanto ela estiver desligada.
 */
class WriteApiService {
  static isEnabled(): boolean {
    return USE_API;
  }

  static isReadEnabled(): boolean {
    return USE_API_READS;
  }

  /**
   * `nextCursor` e `prevCursor` ocupam o lugar que os DocumentSnapshot tinham:
   * o data-grid trata esses valores como opacos e apenas os devolve, entao a
   * troca nao exige mudanca na tabela.
   */
  static listExclusions(
    projectId: string,
    params: {
      limit: number;
      cursor?: string;
      direction?: 'next' | 'prev';
      orderBy?: string;
      order?: 'asc' | 'desc';
      filterField?: string;
      filterValue?: string;
    }
  ): Promise<{
    results: any[];
    totalRegisters: number;
    nextCursor: string | null;
    prevCursor: string | null;
  }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && `${value}`.length > 0) query.set(key, `${value}`);
    });

    return ApiClient.get(`/projects/${projectId}/exclusions?${query.toString()}`);
  }

  /** Totais agregados no backend: a listagem nao sabe recortar por intervalo. */
  static exclusionStats(
    projectId: string,
    params: { from: string; to: string }
  ): Promise<{ byReason: { reason: string; total: number }[]; total: number }> {
    const query = new URLSearchParams({ from: params.from, to: params.to });

    return ApiClient.get(`/projects/${projectId}/exclusions/stats?${query.toString()}`);
  }

  /**
   * Percorre todas as paginas ate o fim.
   *
   * O CacheProvider precisa da colecao inteira; a diferenca em relacao ao SDK e
   * que a leitura sai do browser e passa pelo backend, autorizada por projeto.
   */
  static async listAllContacts(projectId: string): Promise<any[]> {
    const todos: any[] = [];
    let cursor: string | null = null;

    for (;;) {
      const pagina: { results: any[]; nextCursor: string | null } = await ApiClient.get(
        `/projects/${projectId}/contacts?limit=500${cursor === null ? '' : `&cursor=${cursor}`}`
      );

      todos.push(...pagina.results);

      // Pagina menor que o limite significa fim; sem isso um nextCursor
      // estavel faria o laco girar para sempre.
      if (pagina.results.length < 500 || pagina.nextCursor === null) break;

      cursor = pagina.nextCursor;
    }

    return todos;
  }

  // --- administracao de projetos (restrita ao perfil admin) ---

  static listManagedProjects(): Promise<any[]> {
    return ApiClient.get('/admin/projects');
  }

  static createProject(payload: Record<string, unknown>): Promise<any> {
    return ApiClient.post('/admin/projects', payload);
  }

  static updateProject(projectId: string, payload: Record<string, unknown>): Promise<any> {
    return ApiClient.request(`/admin/projects/${projectId}`, { method: 'PATCH', body: payload });
  }

  // --- gestao de acessos (restrita ao perfil admin) ---

  static listUsers(): Promise<any[]> {
    return ApiClient.get('/users');
  }

  static createUser(payload: Record<string, unknown>): Promise<any> {
    return ApiClient.post('/users', payload);
  }

  static updateUser(userId: string, payload: Record<string, unknown>): Promise<any> {
    return ApiClient.request(`/users/${userId}`, { method: 'PATCH', body: payload });
  }

  static setUserActive(userId: string, active: boolean): Promise<any> {
    return ApiClient.request(`/users/${userId}/status`, { method: 'PATCH', body: { active } });
  }

  static getProject(projectId: string): Promise<any> {
    return ApiClient.get(`/projects/${projectId}`);
  }

  static listProjects(): Promise<any[]> {
    return ApiClient.get('/projects');
  }

  static getContact(projectId: string, contactId: number): Promise<any> {
    return ApiClient.get(`/projects/${projectId}/contacts/${contactId}`);
  }

  static getCollectResponse(projectId: string, collectNumber: number, contactId: number): Promise<any> {
    return ApiClient.get(`/projects/${projectId}/collects/${collectNumber}/responses/${contactId}`);
  }

  static saveCollectAnswers(
    projectId: string,
    collectNumber: number,
    contactId: number,
    questions: unknown[]
  ): Promise<void> {
    return ApiClient.request<void>(`/projects/${projectId}/collects/${collectNumber}/responses/${contactId}`, {
      method: 'PATCH',
      body: { questions },
    });
  }

  static finishCollect(
    projectId: string,
    collectNumber: number,
    contactId: number,
    questions?: unknown[]
  ): Promise<void> {
    return ApiClient.post<void>(`/projects/${projectId}/collects/${collectNumber}/responses/${contactId}/finish`, {
      questions,
      gratefulEnabled: true,
    });
  }

  static countContacts(projectId: string): Promise<{ total: number }> {
    return ApiClient.get(`/projects/${projectId}/contacts/count`);
  }

  static collectStats(projectId: string, collectNumber: number): Promise<{ pending: number; answered: number }> {
    return ApiClient.get(`/projects/${projectId}/contacts/collects/${collectNumber}/stats`);
  }

  static registerContact(projectId: string, payload: ApiRegisterContact): Promise<{ contactId: number }> {
    return ApiClient.post<{ contactId: number }>(`/projects/${projectId}/contacts`, payload);
  }

  static registerExclusion(projectId: string, payload: ApiRegisterExclusion): Promise<{ exclusionId: number }> {
    return ApiClient.post<{ exclusionId: number }>(`/projects/${projectId}/exclusions`, payload);
  }

  static updateCalendarStatus(
    projectId: string,
    eventId: string,
    changes: { status?: string; gratefulStatus?: string; sentOn?: string }
  ): Promise<void> {
    return ApiClient.request<void>(`/projects/${projectId}/calendar/${eventId}/status`, {
      method: 'PATCH',
      body: changes,
    });
  }

  /**
   * Log e fire-and-forget: falhar aqui nao pode interromper a acao do usuario.
   *
   * `scope` e o projectId ou `core`. Eventos de sessao -- login, validacao de
   * acesso e logout -- nao pertencem a projeto nenhum e vao para a rota de
   * core; manda-los como se `core` fosse um projeto resultava em 403.
   */
  static writeLog(scope: string, entry: ApiLogEntry): void {
    const path = scope === 'core' ? '/logs' : `/projects/${scope}/logs`;

    ApiClient.post<void>(path, entry).catch(() => undefined);
  }
}

export { WriteApiService };
